import { RouterModule, Routes, NoPreloading } from '@angular/router';
import { AuthGuard } from '@core/auth-guard';
import { PermissionGuard } from '@core/permission-guard';
import { PERMISSION_KEYS } from '@modules/shared/constants/permissions.contant';
import { ROUTE_PATHS } from '@constants/routes';
import { WhitelabelV2Guard } from '@core/whitelabel-v2.guard';
import { OptOutRedirectGuard } from '@core/opt-out-redirect.guard';
import { CreatorApprovalGuard } from '@core/creator-approval.guard';
import { WlsOwnerGuard } from '@core/wls-owner.guard';
import { SIDEBAR_TABS } from '@shared/constants/tab-approval.constant';
import { MARKETING_PAGE_ROUTES } from '@constants/marketing-page-routes.generated';
import { COMPARISON_PAGE_ROUTES } from '@constants/comparison-page-routes.generated';
import { MARKETING_PRO_PAGE_ROUTES } from '@constants/marketing-pro-page-routes.generated';

const permissionKeys = PERMISSION_KEYS;
const marketingPageRoutes: Routes = MARKETING_PAGE_ROUTES.map((route) => ({
  ...route,
  canActivate: [WhitelabelV2Guard]
}));
const comparisonPageRoutes: Routes = COMPARISON_PAGE_ROUTES.map((route) => ({
  ...route,
  canActivate: [WhitelabelV2Guard]
}));
const marketingProPageRoutes: Routes = MARKETING_PRO_PAGE_ROUTES.map((route) => ({
  ...route,
  canActivate: [WhitelabelV2Guard]
}));
const routes: Routes = [
  {
    path: ROUTE_PATHS.AUTH,
    loadChildren: () =>
      import(/* webpackChunkName: "auth" */ './modules/auth/auth.module').then((m) => m.AuthModule),
    canActivate: [AuthGuard]
  },
  {
    path: ROUTE_PATHS.BIO,
    loadChildren: () => import('./modules/link-in-bio-fan-preview/link-in-bio-fan-preview.module').then(m => m.LinkInBioFanPreviewModule)
  },
  {
    path: ROUTE_PATHS.DASHBOARD,
    loadChildren: () =>
      import('./modules/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      )
  },
  {
    path: ROUTE_PATHS.FORUM,
    loadChildren: () =>
      import('./modules/forum/forum.module').then((m) => m.ForumModule),
    canActivate: [AuthGuard, PermissionGuard],
    data: {
      delegatePath: [permissionKeys.MAKE_EDIT_CHAT_ROOM_POSTS]
    }
  },
  {
    path: ROUTE_PATHS.MONETIZE,
    loadChildren: () =>
      import('./modules/monetize/monetize.module').then(
        (m) => m.MonetizeModule
      ),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard, WlsOwnerGuard],
    data: {
      path: permissionKeys.MONETIZE,
      tabKey: SIDEBAR_TABS.MONETIZE
    }
  },
  {
    path: ROUTE_PATHS.EDIT_PREFERENCE,
    loadChildren: () =>
      import('./modules/edit-preference/edit-preference.module').then(
        (m) => m.EditPreferenceModule
      ),
    canActivate: [PermissionGuard],
    data: {
      delegatePath: [permissionKeys.EDIT_MY_PREFERENCES]
    }
  },
  {
    path: ROUTE_PATHS.OPT_OUT,
    children: [],
    canActivate: [OptOutRedirectGuard]
  },
  {
    path: ROUTE_PATHS.CONTENT,
    loadChildren: () =>
      import('./modules/setup-profile/setup-profile.module').then(
        (m) => m.SetupProfileModule
      ),
    canActivate: [AuthGuard]
  },
  {
    path: ROUTE_PATHS.POST,
    loadChildren: () =>
      import('./modules/post-detail/post-detail.module').then(
        (m) => m.PostDetailModule
      )
  },
  {
    path: ROUTE_PATHS.EXPLORE_CREATORS_ROUTE,
    loadChildren: () =>
      import('./modules/creators/creators.module').then(
        (m) => m.CreatorsModule
      ),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard],
    data: {
      path: permissionKeys.CREATORS,
      tabKey: SIDEBAR_TABS.CREATORS
    }
  },
  {
    path: ROUTE_PATHS.WALLET,
    loadChildren: () =>
      import('./modules/wallet/wallet.module').then((m) => m.WalletModule),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard],
    data: {
      path: permissionKeys.WALLET,
      tabKey: SIDEBAR_TABS.WALLET
    }
  },
  {
    path: ROUTE_PATHS.ORDER_STATUS,
    loadChildren: () =>
      import(
        './modules/public/fulfillment-order/fulfillment-order.module'
      ).then((m) => m.FulfillmentOrderModule)
  },
  {
    path: ROUTE_PATHS.MESSAGES,
    loadChildren: () =>
      import('./modules/messages/messages.module').then(
        (m) => m.MessagesModule
      ),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard],
    data: {
      tabKey: SIDEBAR_TABS.MESSAGES,
      delegatePath: [permissionKeys.SEND_REPLY_TO_DIRECT_MESSAGES]
    }
  },
  {
    path: ROUTE_PATHS.INSIGHTS,
    loadChildren: () =>
      import('./modules/insights/insights.module').then(
        (m) => m.InsightsModule
      ),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard, WlsOwnerGuard],
    data: {
      path: permissionKeys.INSIGHTS,
      tabKey: SIDEBAR_TABS.INSIGHTS,
      delegatePath: [
        permissionKeys.ACCESS_USER_SEGMENTATION,
        permissionKeys.VIEW_EARNING_METRICS,
        permissionKeys.VIEW_ENGAGEMENT_METRICS,
        permissionKeys.FAN_MANAGEMENT
      ]
    }
  },
  {
    path: ROUTE_PATHS.PRIVACY_POLICY,
    loadChildren: () =>
      import('./modules/public/privacy-policy/privacy-policy.module').then(
        (m) => m.PrivacyPolicyModule
      )
  },
  {
    path: ROUTE_PATHS.CREATOR,
    loadChildren: () =>
      import('./modules/creators-home/creators-home.module').then(
        (m) => m.CreatorsHomeModule
      ),
    canActivate: [WhitelabelV2Guard]
  },
  {
    path: ROUTE_PATHS.TERMS_CONDITIONS,
    loadChildren: () =>
      import('./modules/public/terms-condition/terms-condition.module').then(
        (m) => m.TermsConditionModule
      )
  },
  {
    path: ROUTE_PATHS.CONTACT_US,
    loadChildren: () =>
      import('./modules/public/contact-us/contact-us/contact-us.module').then(
        (m) => m.ContactUsModule
      )
  },
  {
    path: ROUTE_PATHS.REQUEST_DEMO,
    loadChildren: () =>
      import('./modules/public/contact-us/contact-v1/contact-v1.module').then(
        (m) => m.ContactV1Module
      )
  },
  {
    path: ROUTE_PATHS.SCHEDULE_A_DEMO,
    loadChildren: () =>
      import('./modules/public/contact-us/contact-v1/contact-v1.module').then(
        (m) => m.ContactV1Module
      )
  },
  {
    path: ROUTE_PATHS.UNSUBSCRIBE_INSTRUCTION,
    loadChildren: () =>
      import(
        './modules/public/unsubscribe-instruction/unsubscribe-instruction.module'
      ).then((m) => m.UnsubscribeInstructionModule)
  },
  {
    path: ROUTE_PATHS.NEWSLETTER_UNSUBSCRIBE,
    loadChildren: () =>
      import('./modules/public/unsubscribe-newsletter/unsubscribe-newsletter.module').then((m) => m.UnsubscribeNewsletterModule)
  },
  {
    path: ROUTE_PATHS.IMPORTED_OPT_OUT,
    loadChildren: () =>
      import('./modules/public/imported-opt-out/imported-opt-out.module').then((m) => m.ImportedOptOutModule)
  },
  {
    path: ROUTE_PATHS.ABOUT,
    loadChildren: () =>
      import('./modules/public/about/about.module').then((m) => m.AboutModule),
    canActivate: [WhitelabelV2Guard]
  },
  {
    path: ROUTE_PATHS.PRICING,
    loadChildren: () =>
      import('./modules/public/pricing/pricing.module').then(
        (m) => m.PricingModule
      ),
    canActivate: [WhitelabelV2Guard]
  },
  {
    path: ROUTE_PATHS.SHOPIFY_CONNECT,
    loadChildren: () =>
      import('./modules/public/shopify-connect/shopify-connect.module').then(
        (m) => m.shopifyModule
      )
  },
  {
    path: ROUTE_PATHS.CALENDAR_CONNECT,
    loadChildren: () =>
      import('./modules/public/calendar-connect/calendar-connect.module').then(
        (m) => m.CalendarConnectModule
      )
  },
  {
    path: ROUTE_PATHS.ADVERTISE,
    loadChildren: () =>
      import('./modules/advertisement-home/advertisement-home.module').then(
        (m) => m.AdvertisementHomeModule
      ),
    canActivate: [WhitelabelV2Guard]
  },
  {
    path: ROUTE_PATHS.GLOBAL,
    loadChildren: () =>
      import('./modules/global/global.module').then((m) => m.GlobalModule)
  },
  {
    path: ROUTE_PATHS.TERMS_OF_SERVICE,
    loadChildren: () =>
      import('./modules/public/terms-condition/terms-condition.module').then(
        (m) => m.TermsConditionModule
      )
  },
  ...comparisonPageRoutes,
  ...marketingProPageRoutes,
  {
    path: ROUTE_PATHS.HELP_FAQ,
    loadChildren: () =>
      import('./modules/promotional/help-faq/help-faq.module').then(
        (m) => m.HelpFaqModule
      )
  },
  {
    path: ROUTE_PATHS.SHARE,
    loadChildren: () =>
      import('./modules/public/shortend-url/shortend-url.module').then(
        (m) => m.ShortendUrlModule
      )
  },
  {
    path: ROUTE_PATHS.ACCESS_USER,
    loadChildren: () =>
      import(
        './modules/public/access-user-account/access-user-account.module'
      ).then((m) => m.AccessUserAccountModule)
  },
  {
    path: ROUTE_PATHS.RESOURCES,
    loadChildren: () =>
      import('./modules/resources/resources.module').then(
        (m) => m.ResourcesModule
      ),
    canActivate: [AuthGuard, PermissionGuard, CreatorApprovalGuard, WlsOwnerGuard],
    data: {
      path: permissionKeys.RESOURCES,
      tabKey: SIDEBAR_TABS.RESOURCES
    }
  },
  {
    path: ROUTE_PATHS.CREATORS,
    loadChildren: () =>
      import('./modules/creators-home/creators-home.module').then(
        (m) => m.CreatorsHomeModule
      ),
    canActivate: [WhitelabelV2Guard]
  },
  {
    path: ROUTE_PATHS.NOT_FOUND,
    loadChildren: () =>
      import('./modules/public/not-found/not-found.module').then(
        (m) => m.NotFoundModule
      )
  },
  ...marketingPageRoutes,
  {
    path: 'wls/:id',
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule)
  },
  {
    path: ROUTE_PATHS.PROFILE,
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule)
  },
  {
    path: ROUTE_PATHS.HOME,
    loadChildren: () =>
      import('./modules/promotional/promotional.module').then(
        (m) => m.PromotionalModule
      )
  },
  {
    path: ROUTE_PATHS.WILDCARD,
    loadChildren: () =>
      import('./modules/public/not-found/not-found.module').then(
        (m) => m.NotFoundModule
      )
  }
];

export const ProjectRouting = RouterModule.forRoot(routes, {
  initialNavigation: 'enabledBlocking',
  scrollPositionRestoration: 'top',
  preloadingStrategy: NoPreloading, // PERFORMANCE FIX: Disable aggressive preloading to reduce initial bundle by 25MB
  anchorScrolling: 'enabled', // Enable smooth scrolling to anchors
  onSameUrlNavigation: 'reload', // Reload components when navigating to the same URL
  paramsInheritanceStrategy: 'always', // Always inherit route parameters
  urlUpdateStrategy: 'deferred', // Defer URL updates until navigation completes
  scrollOffset: [0, 64], // Offset for header height
  enableTracing: false // Set to true for debugging only
});
